import { collection, doc, getDocs, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { runTransaction, writeBatch } from 'firebase/firestore';
import { Question } from '../types';

export const updateQuestions = async (conferenceCode: string, questions: Question[]) => {
    const batch = writeBatch(db);
    const conferenceRef = doc(db, 'conferences', conferenceCode);
    const questionsRef = collection(conferenceRef, 'questions');

    // Get all existing questions
    const existingQuestionsSnapshot = await getDocs(questionsRef);
    const existingQuestionIds = new Set(existingQuestionsSnapshot.docs.map(doc => doc.id));
    const newQuestionIds = new Set(questions.map(q => q.id));

    // Delete questions that are no longer in the new array
    existingQuestionIds.forEach(id => {
        if (!newQuestionIds.has(id)) {
            const qRef = doc(questionsRef, id);
            batch.delete(qRef);
        }
    });

    // Update or create questions
    questions.forEach((question) => {
        const qRef = doc(questionsRef, question.id);
        // exclude the id from the question object
        const { id, ...questionData } = question;
        batch.set(qRef, { ...questionData });
    });

    await batch.commit();
};

// getQuestions(conferenceCode: string): Promise<Question[]>
//  Return array of the question objects, sorted by index. 
//  Throw an error if the conference does not exist.
export const getQuestions = async (conferenceCode: string): Promise<Question[]> => {
    const conferenceRef = doc(db, 'conferences', conferenceCode);
    // validate conference exists
    const conferenceDoc = await getDoc(conferenceRef);
    if (!conferenceDoc.exists()) {
        throw new Error('Conference does not exist');
    }
    const questionsRef = collection(conferenceRef, 'questions');
    const questionsSnapshot = await getDocs(questionsRef);
    const questions: Question[] = questionsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id })) as Question[];

    return questions.sort((a, b) => a.index - b.index);
}

// submitResponses(conferenceCode: string, userId: string, responses: Record<string, any>)
//  Record a set of responses to the questions. The user passes a map of question IDs to responses.
//  The user may pass responses to only a subset of the questions. We are storying 2 copies of the reponses
//  in the conference document: a copy of each user's response to each question and a copy of the question's responses.
//  Also, set the user's formSubmitted field to true.

export const submitResponses = async (conferenceCode: string, userId: string, responses: Record<string, any>|null) => {
    if(!responses) {
        // just mark as form submitted
        const userRef = doc(db, 'users', userId);
        await runTransaction(db, async (transaction) => {
            transaction.update(userRef, {
                formSubmitted: true
            });
        });
        return;
    }
  
    await runTransaction(db, async (transaction) => {
    for (const [questionId, answer] of Object.entries(responses)) {
      // Path 1: Per-question responses
      const questionResponseRef = doc(
        db,
        'conferences',
        conferenceCode,
        'questions',
        questionId,
        'responses',
        userId
      );

      // Path 2: Per-user responses
      const userResponseRef = doc(
        db,
        'users',
        userId,
        'responses',
        questionId
      );

      transaction.set(questionResponseRef, {
        answer,
        timestamp: new Date()
      });

      transaction.set(userResponseRef, {
        answer,
        questionId,
        timestamp: new Date()
      });
    }
    // set the user's formSubmitted field to true
    const userRef = doc(db, 'users', userId);
    transaction.update(userRef, {
        formSubmitted: true
    });
  });
}

// getUserResponses(conferenceCode: string, userId: string)
//  Return a map of question IDs to responses for this user only.
export const getUserResponses = async (userId: string) => {
    const usersRef = doc(db, 'users', userId);
    // valide user exists
    const userDoc = await getDoc(usersRef);
    if (!userDoc.exists()) {
        throw new Error('User does not exist');
    }
    const questionsRef = collection(usersRef, 'responses');
    const questionsSnapshot = await getDocs(questionsRef);

    const responses: Record<string, any> = {};
    questionsSnapshot.forEach((doc) => {
        responses[doc.id] = doc.data().answer;
    });    

    return responses;
}

// getQuestionResponses(conferenceCode: string, questionId: string)
//  Return a map of user IDs to responses for this question.
export const getQuestionResponses = async (conferenceCode: string, questionId: string) => {
    const questionRef = doc(db, 'conferences', conferenceCode, 'questions', questionId);
    const responsesRef = collection(questionRef, 'responses');
    const responsesSnapshot = await getDocs(responsesRef);

    const responses: Record<string, any> = {};
    responsesSnapshot.forEach((doc) => {
        responses[doc.id] = doc.data();
    });    

    return responses;
}

// getUserDataForResponses(userIds: string[])
//  Return a map of user IDs to user data.
export const getUserDataForResponses = async (userIds: string[]) => {
    const usersRef = collection(db, 'users');
    const usersSnapshot = await getDocs(usersRef);
    
    const userData: Record<string, any> = {};
    usersSnapshot.forEach((doc) => {
        if (userIds.includes(doc.id)) {
            const data = doc.data();
            userData[doc.id] = {
                displayName: data.displayName,
                email: data.email,
                conferenceCode: data.conferenceCode
            };
        }
    });

    return userData;
}